// Backend client.
//
// Same-origin by design. The backend serves this build and the /api routes off
// one host, and auth lives in httpOnly cookies (`whm_access` / `whm_refresh`),
// so every request goes out with `credentials: "include"` against a relative
// path. An absolute cross-origin base would silently drop those cookies and
// every call would 401.
//
// Three things the server requires on the way out:
//   X-Request-ID    a fresh id per request, echoed back and used to find the
//                   matching server log line
//   X-CSRF-Token    the readable `whm_csrf` cookie copied into a header, on
//                   mutating methods only (double-submit)
//   X-Organization-ID  the operator's selected org, when they have switched
//
// And one thing it does on the way back: a 401 triggers exactly one refresh
// attempt, then a single retry of the original request.

const BASE_PATH = ((window as any).__BASE_PATH__ ?? "").replace(/\/$/, "");
const API = `${BASE_PATH}/api`;

const ORG_KEY = "selected_organization_id";

// ------------------------------------------------------------------ errors

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public traceId: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isAuth() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isNotFound() {
    return this.status === 404;
  }
}

// ------------------------------------------------------------------ helpers

function getCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function requestId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** Build a query string, dropping empty and nullish values so unset filters vanish. */
export function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === "" || v === null || v === undefined) continue;
    if (Array.isArray(v)) {
      for (const item of v) if (item !== "" && item != null) u.append(k, String(item));
    } else {
      u.set(k, String(v));
    }
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}

// ------------------------------------------------------------------ core

type Envelope<T> = { status: string; data: T; message?: string };

/** Listeners fired when the session is gone for good, so the shell can show the login screen. */
const authListeners = new Set<() => void>();
export function onAuthLost(fn: () => void): () => void {
  authListeners.add(fn);
  return () => authListeners.delete(fn);
}
function announceAuthLost() {
  for (const fn of authListeners) fn();
}

let refreshInFlight: Promise<boolean> | null = null;

/** Refresh via the httpOnly `whm_refresh` cookie. Concurrent 401s share one attempt. */
async function refresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${API}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "X-Request-ID": requestId() },
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => {
        // Cleared on the next tick so callers racing this promise all see the
        // same result before a new attempt can start.
        setTimeout(() => (refreshInFlight = null), 0);
      });
  }
  return refreshInFlight;
}

async function toError(r: Response, path: string): Promise<ApiError> {
  let code = "internal_error";
  let message = `${r.status} ${r.statusText} on ${path}`;
  let traceId = r.headers.get("X-Request-ID") ?? "";
  let details: Record<string, unknown> | undefined;

  try {
    const body = await r.json();
    // The server sends both the legacy envelope and the structured error.
    if (body?.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      traceId = body.error.trace_id ?? traceId;
      details = body.error.details;
    } else if (body?.message) {
      message = body.message;
    }
  } catch {
    // Not JSON — a proxy error page or an empty body. Keep the status line.
  }
  return new ApiError(r.status, code, message, traceId, details);
}

interface CallOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Skip envelope unwrapping and return the raw Response (downloads, streams). */
  raw?: boolean;
  /** Send a FormData body untouched, without a JSON content-type. */
  form?: FormData;
}

async function call<T>(path: string, opts: CallOptions = {}, isRetry = false): Promise<T> {
  const method = (opts.method ?? "GET").toUpperCase();
  const headers = new Headers(opts.headers);

  headers.set("X-Request-ID", requestId());

  if (MUTATING.has(method)) {
    const csrf = getCookie("whm_csrf");
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }

  const org = localStorage.getItem(ORG_KEY);
  if (org) headers.set("X-Organization-ID", org);

  let body: BodyInit | undefined;
  if (opts.form) {
    // Let the browser set the multipart boundary.
    body = opts.form;
  } else if (opts.body !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(opts.body);
  }

  const res = await fetch(`${API}${path}`, {
    ...opts,
    method,
    headers,
    body,
    credentials: "include",
  });

  if (res.status === 401 && !isRetry && !path.startsWith("/auth/")) {
    if (await refresh()) return call<T>(path, opts, true);
    announceAuthLost();
  }

  if (!res.ok) throw await toError(res, path);

  if (opts.raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;

  const payload = (await res.json()) as Envelope<T> | T;
  // Unwrap {status, data}. A handful of endpoints answer unwrapped, so a body
  // without the envelope is passed through rather than becoming undefined.
  if (payload && typeof payload === "object" && "status" in payload && "data" in payload) {
    return (payload as Envelope<T>).data;
  }
  return payload as T;
}

// ------------------------------------------------------------------ verbs

export const http = {
  get: <T>(path: string) => call<T>(path),
  post: <T>(path: string, body?: unknown) => call<T>(path, { method: "POST", body }),
  put: <T>(path: string, body?: unknown) => call<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown) => call<T>(path, { method: "PATCH", body }),
  del: <T>(path: string, body?: unknown) => call<T>(path, { method: "DELETE", body }),
  upload: <T>(path: string, form: FormData, method: "POST" | "PUT" = "POST") =>
    call<T>(path, { method, form }),
  raw: (path: string, opts: CallOptions = {}) => call<Response>(path, { ...opts, raw: true }),
};

// ------------------------------------------------------------------ org

export const org = {
  selected: () => localStorage.getItem(ORG_KEY),
  select: (id: string | null) => {
    if (id) localStorage.setItem(ORG_KEY, id);
    else localStorage.removeItem(ORG_KEY);
  },
};

// ------------------------------------------------------------------ streams

/**
 * Server-sent events over POST, for the Model Lab streaming run.
 *
 * EventSource cannot POST and cannot set headers, so this reads the response
 * body directly and splits SSE frames itself. `onEvent` gets one decoded frame
 * at a time; the returned function aborts the run.
 */
export function stream(
  path: string,
  body: unknown,
  onEvent: (event: string, data: string) => void,
  onDone?: (err?: Error) => void
): () => void {
  const controller = new AbortController();
  const headers = new Headers({
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    "X-Request-ID": requestId(),
  });
  const csrf = getCookie("whm_csrf");
  if (csrf) headers.set("X-CSRF-Token", csrf);
  const orgId = localStorage.getItem(ORG_KEY);
  if (orgId) headers.set("X-Organization-ID", orgId);

  (async () => {
    try {
      const res = await fetch(`${API}${path}`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
        credentials: "include",
        signal: controller.signal,
      });
      if (!res.ok) throw await toError(res, path);
      if (!res.body) throw new Error("stream has no body");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Frames are separated by a blank line.
        let split: number;
        while ((split = buffer.indexOf("\n\n")) !== -1) {
          const frame = buffer.slice(0, split);
          buffer = buffer.slice(split + 2);

          let name = "message";
          const dataLines: string[] = [];
          for (const line of frame.split("\n")) {
            if (line.startsWith("event:")) name = line.slice(6).trim();
            else if (line.startsWith("data:")) dataLines.push(line.slice(5).trimStart());
          }
          if (dataLines.length) onEvent(name, dataLines.join("\n"));
        }
      }
      onDone?.();
    } catch (err) {
      if ((err as Error)?.name === "AbortError") onDone?.();
      else onDone?.(err as Error);
    }
  })();

  return () => controller.abort();
}

// ------------------------------------------------------------------ websocket

/**
 * Operator live updates.
 *
 * The browser WebSocket API cannot send an Authorization header, so the
 * handshake uses a short-lived token fetched from /auth/ws-token and passed in
 * the query string. Reconnects fetch a fresh one — the token outlives the
 * connection attempt and nothing more.
 */
export function connectSocket(
  onMessage: (type: string, payload: unknown) => void,
  onStatus?: (up: boolean) => void
): () => void {
  let socket: WebSocket | null = null;
  let closed = false;
  let attempt = 0;
  let timer: number | undefined;

  const open = async () => {
    if (closed) return;
    try {
      const { token } = await http.get<{ token: string }>("/auth/ws-token");
      if (closed) return;

      const proto = location.protocol === "https:" ? "wss:" : "ws:";
      socket = new WebSocket(`${proto}//${location.host}${BASE_PATH}/ws?token=${encodeURIComponent(token)}`);

      socket.onopen = () => {
        attempt = 0;
        onStatus?.(true);
      };
      socket.onmessage = (ev) => {
        try {
          const frame = JSON.parse(ev.data);
          onMessage(frame.type ?? "message", frame.payload ?? frame.data ?? frame);
        } catch {
          // A frame we cannot parse is not worth tearing the socket down for.
        }
      };
      socket.onclose = () => {
        onStatus?.(false);
        retry();
      };
      socket.onerror = () => socket?.close();
    } catch {
      retry();
    }
  };

  const retry = () => {
    if (closed) return;
    // Exponential backoff to 30s, so a backend restart does not turn into a
    // reconnect storm from every open console.
    const delay = Math.min(30_000, 1000 * 2 ** attempt++);
    timer = window.setTimeout(open, delay);
  };

  void open();

  return () => {
    closed = true;
    if (timer) window.clearTimeout(timer);
    socket?.close();
  };
}
